import React, { useEffect, useState } from 'react';
import Editor from '@monaco-editor/react';
import './Editor.css';
import { getRequest, postRequest } from "../../Fetch/request";
import { useLocation } from 'react-router-dom';
import Loader from '../Loader/Loader';
import { getUserId } from '../../Utils/authUtils';

const EditorComponent = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    // Example: Getting specific query parameter
    const problemId = queryParams.get('problemId');

    const [language, setLanguage] = useState('cpp'); // Default language is C++
    const [code, setCode] = useState(''); // Will be set from localStorage or backend
    const [activeTab, setActiveTab] = useState('description'); // Manage active tab state
    const [showTestCases, setShowTestCases] = useState(false); // Manage test cases visibility
    const [runSubmit, setRunSubmit] = useState("");
    const [warnings, setWarnings] = useState([]);
    const [errors, setErrors] = useState([]);
    const [errorType, setErrorType] = useState("");
    const [output, setOutput] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const sampleProblemDetails = {
        title: "",
        description: "",
        sampleTestCases: [],
        constraints: [],
        inputFormat: "",
        outputFormat: "",
        hints: [],
        sampleTestCasesFile: [],
        completeTestCasesFile: [],
        correctSolutionFile: [],
        functionDefinition: ""
    };
    const [problemDetails, setProblemDetails] = useState(sampleProblemDetails);

    const [timer, setTimer] = useState(0); // Timer in seconds

    const [isProblemLoading, setIsProblemLoading] = useState(false);
    const [isResultLoading, setIsResultLoading] = useState(false);

    useEffect(() => {
        let interval = null;
        if (problemDetails.title) {
            interval = setInterval(() => {
                setTimer((prevTime) => prevTime + 1);
            }, 1000);
        }
        return () => clearInterval(interval); // Clear interval on component unmount
    }, [problemDetails]);

    const handleLanguageChange = (e) => {
        const newLanguage = e.target.value;
        setLanguage(newLanguage);
        
        // Get the current function definition (remove headers if present)
        let currentFunctionDefinition = code;
        if (language === 'cpp') {
            // Remove C++ headers if they exist (both old and new format)
            currentFunctionDefinition = code.replace(/^#include <bits\/stdc\+\+\.h>\nusing namespace std;\n\n/, '');
            currentFunctionDefinition = currentFunctionDefinition.replace(/^#include <iostream>\nusing namespace std;\n\n\/\/ Add additional headers as needed.*?\n\n/, '');
        }
        
        // Set default code based on selected language
        let newCode = '';
        switch (newLanguage) {
            case 'cpp':
                const defaultHeaders = '#include <iostream>\nusing namespace std;\n\n// Add additional headers as needed.\n\n';
                newCode = defaultHeaders + currentFunctionDefinition;
                break;
            default:
                newCode = '';
        }
        
        setCode(newCode);
        
        // Save to localStorage
        if (problemId) {
            const storageKey = `editor-${problemId}`;
            const storageData = {
                code: newCode,
                language: newLanguage
            };
            localStorage.setItem(storageKey, JSON.stringify(storageData));
        }
    };

    const getLanguageExtension = () => {
        switch (language) {
            case 'cpp':
                return 'cpp';
            default:
                return 'cpp';
        }
    };

    const runCode = async (type) => {
        const userId = getUserId();

        const runCodeObject = {
            language: language,
            code: code,
            operation: type,
            problem: problemDetails,
            solutionViewed: false,
            hintsViewed: false,
            userId: userId,
            timeSpent: timer // Add time spent
        };

        setIsResultLoading(true);
        try {
            // Make single API call
            const postResponse = await postRequest(
                process.env.REACT_APP_URL + "/api/lambda/cpp/execute",
                runCodeObject
            );
            

            
            // Process the response
            setIsResultLoading(false);
            if (postResponse && postResponse.status === "200") {
                // Extract the result from the new response structure
                const result = postResponse.response?.result;
                
                if (result) {
                    setErrorType(result.errorType);
                    setWarnings(result.warnings || []);
                    setErrors(result.errors || []);
                    setSubmitSuccess(result.success);
                    setOutput(result.outputs || []);
                    setRunSubmit(type);
                } else {
                    setErrorType("COMPILATION_ERROR");
                    setErrors(["No result found in response"]);
                    setSubmitSuccess(false);
                    setRunSubmit(type);
                }
            } else {
                setErrorType("COMPILATION_ERROR");
                setErrors(["Request failed. Please try again."]);
                setSubmitSuccess(false);
                setRunSubmit(type);
            }
        } catch (error) {
            setIsResultLoading(false);
            setErrorType("COMPILATION_ERROR");
            setErrors(["Error: " + error.message]);
            setSubmitSuccess(false);
            setRunSubmit(type);
        }
    };

    useEffect(() => {
    }, [output]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const userId = getUserId();

                const url = `${process.env.REACT_APP_URL}/problems?problemId=${problemId}&userId=${userId}`;
                setIsProblemLoading(true);
                const getResponse = await getRequest(url);
                setIsProblemLoading(false);
                if (getResponse.status === "200" && getResponse.response != null && getResponse.response.problems != null && getResponse.response.problems.length > 0) {
                    const problem = getResponse.response.problems[0];
                    setProblemDetails(problem);
                    
                    // Check localStorage first
                    const storageKey = `editor-${problemId}`;
                    const savedData = localStorage.getItem(storageKey);
                    
                    if (savedData) {
                        // Use localStorage data
                        try {
                            const parsedData = JSON.parse(savedData);
                            setCode(parsedData.code || '');
                            setLanguage(parsedData.language || 'cpp');
                        } catch (e) {
                            // Fallback to backend code
                            setCodeFromBackend(problem);
                        }
                    } else {
                        // No localStorage data, use backend code
                        setCodeFromBackend(problem);
                    }
                }
            } catch (error) {
                // Error fetching data
            }
        };
        
        const setCodeFromBackend = (problem) => {
            if(problem.functionDefinition){
                // Append default C++ headers to the fetched function definition
                const defaultHeaders = '#include <iostream>\nusing namespace std;\n\n// Add additional headers as needed.\n\n';
                setCode(defaultHeaders + problem.functionDefinition);
            } else {
                // If no function definition, set default C++ headers
                setCode('#include <iostream>\nusing namespace std;\n\n// Add additional headers as needed.\n\n');
            }
        };
        
        if( problemId !== null ) {
            fetchData(); // Call the async function
        }
    }, [problemId]);

    return (
        <div className="editor-page">
            <div className="editor-container">
            {/* Problem Details Section */}
                <div className="problem-panel">
                    {isProblemLoading ? (<Loader />) : (<>
                <div className="problem-header">
                    <h2>{problemDetails.problemId}. {problemDetails.title}</h2>
                    <div className="timer" style={{"float":"right",color:"yellow"}}>
                        <h2>
                            Time: {Math.floor(timer / 60)}m {timer % 60}s
                        </h2>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="tabs">
                    <button onClick={() => setActiveTab('description')} className={activeTab === 'description' ? 'active' : ''}>
                        Description
                    </button>
                    <button onClick={() => setActiveTab('solution')} className={activeTab === 'solution' ? 'active' : ''}>
                        Solution
                    </button>
                </div>

                {/* Tab Content */}
                <div style={{
                    display: activeTab === 'description' ? 'block' : 'none'
                }}>
                    <h5>Description</h5>
                    <div
                        dangerouslySetInnerHTML={{__html: problemDetails.description}}
                        style={{
                            fontFamily: 'Arial, sans-serif',
                            marginLeft: '5px'
                        }}
                    />

                    <h5>Examples</h5>
                    <div style={{marginLeft: '10px', whiteSpace: 'pre-wrap'}}>
                        {problemDetails.sampleTestCases ? (
                            (() => {
                                let input = "", output = "";
                                
                                // Handle both string and array formats
                                if (typeof problemDetails.sampleTestCases === 'string') {
                                    // Parse the combined input/output string
                                    const testCaseStr = problemDetails.sampleTestCases;
                                    
                                    // Split by "Output:" to separate input and output
                                    const parts = testCaseStr.split(/Output:\s*/i);
                                    if (parts.length >= 2) {
                                        input = parts[0].replace(/^Input:\s*/i, '').trim();
                                        output = parts[1].trim();
                                    } else {
                                        // If no "Output:" found, treat the whole string as input
                                        input = testCaseStr.replace(/^Input:\s*/i, '').trim();
                                        output = "";
                                    }
                                } else if (Array.isArray(problemDetails.sampleTestCases) && problemDetails.sampleTestCases.length > 0) {
                                    // Handle array format - each element might be a string with Input/Output
                                    const tc = problemDetails.sampleTestCases[0];
                                    if (typeof tc === 'string') {
                                        // Check if the string contains both Input and Output
                                        if (tc.includes('Input:') && tc.includes('Output:')) {
                                            // Split by "Output:" to separate input and output
                                            const parts = tc.split(/Output:\s*/i);
                                            if (parts.length >= 2) {
                                                input = parts[0].replace(/^Input:\s*/i, '').trim();
                                                output = parts[1].trim();
                                            } else {
                                                input = tc.replace(/^Input:\s*/i, '').trim();
                                                output = "";
                                            }
                                        } else {
                                            // Try to parse as JSON object
                                            try {
                                                const tcObj = JSON.parse(tc);
                                                input = tcObj.input || "";
                                                output = tcObj.output || "";
                                            } catch (e) {
                                                // If JSON parsing fails, treat as input only
                                                input = tc.replace(/^Input:\s*/i, '').trim();
                                                output = "";
                                            }
                                        }
                                    } else {
                                        // Handle object format
                                        input = tc.input || "";
                                        output = tc.output || "";
                                    }
                                }
                                
                                // Clean up input
                                const cleanInput = input
                                    .replace(/^Input:\s*/i, '')
                                    .replace(/Input:\s*/gi, '')
                                    .replace(/^Input\s*/i, '')
                                    .replace(/Input\s*/gi, '')
                                    .trim();
                                
                                // Clean up output - remove ALL "Output:" labels completely
                                let cleanOutput = output;
                                // First, remove any "Output:" from the beginning
                                cleanOutput = cleanOutput.replace(/^Output:\s*/i, '');
                                // Then remove any remaining "Output:" labels anywhere in the text
                                cleanOutput = cleanOutput.replace(/Output:\s*/gi, '');
                                // Also remove standalone "Output" without colon
                                cleanOutput = cleanOutput.replace(/^Output\s*/i, '');
                                cleanOutput = cleanOutput.replace(/Output\s*/gi, '');
                                // Split by lines and remove empty lines
                                const lines = cleanOutput.split('\n');
                                const filteredLines = lines.filter(line => line.trim() !== '');
                                cleanOutput = filteredLines.join('\n').trim();
                                
                                return (
                                    <div style={{marginBottom: '15px'}}>
                                        <div style={{fontWeight: 'bold', marginBottom: '5px'}}>Input:</div>
                                        <div style={{
                                            backgroundColor: '#f5f5f5', 
                                            padding: '8px', 
                                            borderRadius: '4px', 
                                            fontFamily: 'monospace',
                                            fontSize: '13px',
                                            whiteSpace: 'pre-wrap',
                                            marginBottom: '10px'
                                        }}>
                                            {cleanInput}
                                        </div>
                                        <div style={{fontWeight: 'bold', marginBottom: '5px'}}>Output:</div>
                                        <div style={{
                                            backgroundColor: '#f0f8ff', 
                                            padding: '8px', 
                                            borderRadius: '4px', 
                                            fontFamily: 'monospace',
                                            fontSize: '13px',
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {cleanOutput}
                                        </div>
                                    </div>
                                );
                            })()
                        ) : (
                            <div>No examples available</div>
                        )}
                    </div>

                    <h5>Input Format</h5>
                    <div style={{marginLeft: '10px', whiteSpace: 'pre-wrap'}}>
                        {problemDetails.inputFormat}
                    </div>

                    <h5>Output Format</h5>
                    <div style={{marginLeft: '10px', whiteSpace: 'pre-wrap'}}>
                        {problemDetails.outputFormat}
                    </div>

                    <h5>Constraints</h5>
                    {Array.isArray(problemDetails.constraints) ? (
                        <div style={{marginLeft:'16px'}}>
                            {problemDetails.constraints.map((con, idx) => {
                                // Try both literal \n and actual newline characters
                                let constraints = con.split('\\n');
                                if (constraints.length === 1) {
                                    // If no split occurred, try actual newline characters
                                    constraints = con.split('\n');
                                }
                                return (
                                    <div key={idx}>
                                        {constraints.map((constraint, index) => (
                                            <div key={index} style={{ 
                                                fontFamily: 'Arial, sans-serif',
                                                lineHeight: '1.6',
                                                marginBottom: '8px'
                                            }}>
                                                {constraint.replace(/\\le/g, '≤').replace(/\\cdot/g, '·').trim()}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div
                           style={{
                               fontFamily:'Arial, sans-serif',
                               lineHeight:'1.6',
                               marginLeft:'16px',
                               whiteSpace: 'pre-line'
                           }}
                        >
                            {problemDetails.constraints?.split('\\n').map((constraint, index) => (
                                <div key={index} style={{ marginBottom: '8px' }}>
                                    {constraint.replace(/\\le/g, '≤').replace(/\\cdot/g, '·').trim()}
                                </div>
                            )) || ''}
                        </div>
                    )}

                    <h5>Hints</h5>
                    <ul
                        style={{
                            marginLeft: '5px'
                        }}
                    >
                        {problemDetails.hints.map((hint, index) => (
                            <li key={index}>{hint}</li>
                        ))}
                    </ul>
                </div>

                <div style={{
                    display: activeTab === 'solution' ? 'block' : 'none'
                }}>
                    <h3>Solution</h3>
                    <p>This is where the solution explanation would go.</p>
                </div>
             </>)}
            </div>

            {/* Code Editor Section */}
            <div className="code-panel">
                <div className="editor-header">
                    <div className="language-selector">
                        <label htmlFor="language">Language: &nbsp;</label>
                        <select id="language" value={language}
                                onChange={handleLanguageChange}>
                            <option value="cpp">C++</option>
                        </select>
                    </div>
                    <div className="action-buttons-top">
                        <button className="run-button" onClick={() => runCode("run")}>
                            Run
                        </button>
                        <button className="submit-button"
                                onClick={() => runCode(
                                    "submit")}>
                            Submit
                        </button>
                    </div>
                </div>

                {/* Monaco Code Editor */}
                <Editor
                    height="60vh"
                    language={getLanguageExtension()}
                    value={code}
                    theme="vs-dark"
                    onChange={(value) => {
                        setCode(value);
                        // Auto-save to localStorage
                        if (problemId) {
                            const storageKey = `editor-${problemId}`;
                            const storageData = {
                                code: value,
                                language: language
                            };
                            localStorage.setItem(storageKey, JSON.stringify(storageData));
                        }
                    }}
                />

                {/* Results Section */}
                <div style={{ marginTop: '20px', width: '100%' }}>
                {isResultLoading ? (
                    <Loader />
                ) : (<>
                    {runSubmit !== "" && (
                        <div
                            style={{
                                backgroundColor: submitSuccess ? '#e0f7fa' : '#ffebee',
                                borderRadius: '8px',
                                padding: '20px',
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '16px',
                                color: '#333',
                            }}
                        >
                            {/* Correct or Wrong Results */}

                            {submitSuccess &&  (
                                <div>
                                    {/* Display Error Type as Title */}
                                    <h3 style={{ color: '#00796b' }}>
                                        {errorType.replace(/_/g, ' ').toUpperCase()}
                                    </h3>
                                    <table
                                        style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            marginTop: '10px',
                                        }}
                                    >
                                        <thead>
                                        <tr>
                                            <th
                                                style={{
                                                    border: '1px solid #ccc',
                                                    padding: '8px',
                                                    background: '#b2dfdb',
                                                }}
                                            >
                                                Test Case
                                            </th>
                                            <th
                                                style={{
                                                    border: '1px solid #ccc',
                                                    padding: '8px',
                                                    background: '#b2dfdb',
                                                }}
                                            >
                                                Result
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {output.map((line, index) => {
                                            const parts = line.split(':');
                                            if (parts.length === 2) {
                                                const [testCase, result] = parts;
                                                const isWrong = result.includes('Wrong');
                                                const isCorrect = result.includes('Correct');
                                                const isTle = result.includes("TLE")
                                                return (
                                                    <tr
                                                        key={index}
                                                        style={{
                                                            backgroundColor: isWrong
                                                                ? '#ffcccc' // Light red for wrong
                                                                : isCorrect
                                                                    ? '#ccffcc' // Light green for correct
                                                                    : isTle ? '#ffcc80' : 'inherit', // Light orange for TLE
                                                        }}
                                                    >
                                                        <td
                                                            style={{
                                                                border: '1px solid #ccc',
                                                                padding: '8px',
                                                                color: '#333',
                                                            }}
                                                        >
                                                            {testCase.trim()}
                                                        </td>
                                                        <td
                                                            style={{
                                                                border: '1px solid #ccc',
                                                                padding: '8px',
                                                                color: isWrong ? '#d32f2f' : '#388e3c',
                                                            }}
                                                        >
                                                            {result.trim()}
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            return null;
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Errors or Warnings */}
                            {!submitSuccess && (
                                <div>
                                    <h3 style={{ color: '#d32f2f' }}>
                                        {errorType.replace(/_/g, ' ').toUpperCase()}
                                    </h3>
                                    {/* Render Warnings */}
                                    {warnings && warnings.length > 0 && (
                                        <div>
                                            <ul>
                                                {warnings.map((warning, index) => (
                                                    <li key={index}>
                                                        {warning}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Render Errors */}
                                    {errors && errors.length > 0 && (
                                        <div>
                                            <ul>
                                                {errors.map((error, index) => (
                                                    <li key={index}>
                                                        {error}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                    )}
                    </> )}
                </div>


            </div>
        </div>
        </div>
    );
};

export default EditorComponent;
